import * as React from 'react';
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    Stack,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslate } from 'react-admin';
import {
    estimateMergeRemaining,
    formatMergeDuration,
    MergeProgress,
} from './mergeBatch';

interface Props {
    previewOpen: boolean;
    canConfirm: boolean;
    groupCount: number;
    progress: MergeProgress | null;
    stopRequested: boolean;
    onClose: () => void;
    onConfirm: () => void;
    onStop: () => void;
    children?: React.ReactNode;
}

const progressStripes = (color: string) =>
    `linear-gradient(45deg, ${color} 25%, transparent 25%, transparent 50%, ${color} 50%, ${color} 75%, transparent 75%, transparent)`;

const BulkMergeDialog = ({
    previewOpen,
    canConfirm,
    groupCount,
    progress,
    stopRequested,
    onClose,
    onConfirm,
    onStop,
    children,
}: Props) => {
    const translate = useTranslate();
    const running = Boolean(progress && progress.finishedAt === undefined);
    const [now, setNow] = React.useState(() => performance.now());

    React.useEffect(() => {
        if (!running) return;
        setNow(performance.now());
        const timer = setInterval(() => setNow(performance.now()), 1000);
        return () => clearInterval(timer);
    }, [running, progress?.startedAt]);

    const elapsed = progress
        ? formatMergeDuration(
              Math.max(0, (progress.finishedAt ?? now) - progress.startedAt)
          )
        : '';
    const remaining =
        progress && running && !stopRequested
            ? estimateMergeRemaining(progress, now)
            : null;
    const count = progress
        ? translate('merge.bulk.progress_count', {
              completed: progress.processed,
              total: progress.total,
          })
        : '';
    const hasProblems = Boolean(
        progress &&
            (progress.failed ||
                progress.partial ||
                progress.historyErrors ||
                progress.fatalError)
    );

    return (
        <Dialog
            open={previewOpen || Boolean(progress)}
            onClose={() => {
                if (!running) onClose();
            }}
            maxWidth="md"
            fullWidth
            scroll="paper"
            aria-labelledby="bulk-merge-dialog-title"
        >
            <DialogTitle id="bulk-merge-dialog-title">
                {translate(
                    !progress
                        ? 'merge.bulk.preview_title'
                        : running
                          ? 'merge.bulk.progress_title'
                          : 'merge.bulk.summary_title'
                )}
            </DialogTitle>
            <DialogContent dividers>
                {!progress ? (
                    children
                ) : running ? (
                    <Stack spacing={2} sx={{ py: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <CircularProgress size={20} aria-hidden="true" />
                            <Typography role="status">{count}</Typography>
                        </Stack>
                        <LinearProgress
                            variant="determinate"
                            value={(progress.processed / progress.total) * 100}
                            aria-label={count}
                            aria-valuetext={count}
                            sx={theme => ({
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: alpha(
                                    theme.palette.primary.main,
                                    0.12
                                ),
                                backgroundImage: progressStripes(
                                    alpha(theme.palette.primary.main, 0.12)
                                ),
                                backgroundSize: '20px 20px',
                                animation:
                                    'bulkMergeProgressStripes 1s linear infinite',
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 4,
                                    backgroundColor: theme.palette.primary.main,
                                    backgroundImage: progressStripes(
                                        alpha(theme.palette.common.white, 0.25)
                                    ),
                                    backgroundSize: '20px 20px',
                                    animation:
                                        'bulkMergeProgressStripes 1s linear infinite',
                                },
                                '@keyframes bulkMergeProgressStripes': {
                                    '0%': { backgroundPosition: '0 0' },
                                    '100%': { backgroundPosition: '20px 0' },
                                },
                                '@media (prefers-reduced-motion: reduce)': {
                                    animation: 'none',
                                    '& .MuiLinearProgress-bar': {
                                        animation: 'none',
                                        transition: 'none',
                                    },
                                },
                            })}
                        />
                        <Typography>
                            {translate('merge.bulk.progress_elapsed', {
                                time: elapsed,
                            })}
                        </Typography>
                        {!stopRequested && (
                            <>
                                <Typography>
                                    {remaining !== null
                                        ? translate(
                                              'merge.bulk.progress_remaining',
                                              {
                                                  time: formatMergeDuration(
                                                      Math.ceil(
                                                          remaining / 1000
                                                      ) * 1000
                                                  ),
                                              }
                                          )
                                        : translate(
                                              'merge.bulk.progress_estimating'
                                          )}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {translate(
                                        'merge.bulk.progress_estimate_help'
                                    )}
                                </Typography>
                            </>
                        )}
                        {stopRequested && (
                            <Alert severity="warning" role="status">
                                {translate('merge.bulk.progress_stopping')}
                            </Alert>
                        )}
                        <Typography variant="body2" color="text.secondary">
                            {translate('merge.bulk.progress_keep_open')}
                        </Typography>
                    </Stack>
                ) : (
                    <Stack spacing={1.5} sx={{ py: 1 }}>
                        <Alert
                            severity={
                                progress.fatalError || progress.failed
                                    ? 'error'
                                    : progress.stopped || hasProblems
                                      ? 'warning'
                                      : 'success'
                            }
                        >
                            {progress.fatalError
                                ? translate('merge.bulk.summary_fatal_error', {
                                      error: progress.fatalError,
                                  })
                                : translate(
                                      progress.stopped
                                          ? 'merge.bulk.summary_stopped'
                                          : 'merge.bulk.batch_finished'
                                  )}
                        </Alert>
                        <Typography>{count}</Typography>
                        <Typography>
                            {translate('merge.bulk.summary_succeeded', {
                                count: progress.succeeded,
                            })}
                        </Typography>
                        <Typography>
                            {translate('merge.bulk.summary_partial', {
                                count: progress.partial,
                            })}
                        </Typography>
                        <Typography>
                            {translate('merge.bulk.summary_failed', {
                                count: progress.failed,
                            })}
                        </Typography>
                        <Typography>
                            {translate('merge.bulk.summary_unprocessed', {
                                count: progress.total - progress.processed,
                            })}
                        </Typography>
                        <Typography>
                            {translate('merge.bulk.progress_elapsed', {
                                time: elapsed,
                            })}
                        </Typography>
                        {progress.historyErrors > 0 && (
                            <Alert severity="warning">
                                {translate('merge.bulk.summary_history_error', {
                                    count: progress.historyErrors,
                                })}
                            </Alert>
                        )}
                        {progress.stopped &&
                            progress.processed < progress.total && (
                                <Typography variant="body2">
                                    {translate(
                                        'merge.bulk.summary_unprocessed_help'
                                    )}
                                </Typography>
                            )}
                        {hasProblems && (
                            <Typography variant="body2">
                                {translate('merge.bulk.summary_retry_help')}
                            </Typography>
                        )}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                {!progress ? (
                    <>
                        <Button onClick={onClose}>
                            {translate('merge.cancel')}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={onConfirm}
                            disabled={!canConfirm}
                        >
                            {translate('merge.bulk.merge_selected', {
                                count: groupCount,
                            })}
                        </Button>
                    </>
                ) : running ? (
                    <Button
                        color="warning"
                        onClick={onStop}
                        disabled={stopRequested}
                    >
                        {translate('merge.bulk.stop_remaining')}
                    </Button>
                ) : (
                    <Button onClick={onClose}>
                        {translate('ra.action.close')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default BulkMergeDialog;
